import { useEffect, useState } from "react";
import { Cube, Info } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { EditableSelectComponent } from "components/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";

export interface SelectableBuildingAttribute {
  id: number;
  name: string;
  template?: { category?: string };
}

interface Props {
  /** Gebouw waarvan de attributen getoond worden */
  buildingId: string | null;
  /** Geselecteerd attribuut-id (bv. voorgeselecteerd vanuit een huurcontract) */
  value: number | null;
  onSelect: (attribute: SelectableBuildingAttribute | null) => void;
}

/**
 * Attribuut-kaart in de uploadmodal: laat de gebruiker een gebouwattribuut
 * (garage, parking, berging, ...) kiezen zodat het document in de SharePoint-map
 * van dat attribuut belandt i.p.v. bij het gebouw/de unit. Rendert niets wanneer
 * het gekozen gebouw geen attributen heeft.
 */
export const AttributeSelectSection = ({ buildingId, value, onSelect }: Props) => {
  const { session } = useSession();
  const [attributes, setAttributes] = useState<SelectableBuildingAttribute[]>([]);

  useEffect(() => {
    if (!buildingId || !session) {
      setAttributes([]);
      return;
    }
    let cancelled = false;
    const fetchAttributes = async () => {
      try {
        const resp = await fetchWithAuth(
          `${API_URL}/building-attributes/building/${buildingId}`,
          { method: "GET", headers: { "Content-Type": "application/json" } },
          session,
        );
        if (!resp.ok) throw new Error(`Fout bij ophalen attributen: ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setAttributes(Array.isArray(data) ? data : []);
      } catch (err) {
        // Stil falen: de uploadmodal blijft bruikbaar zonder attribuutkeuze.
        console.error("AttributeSelectSection fetchAttributes:", err);
        if (!cancelled) setAttributes([]);
      }
    };
    fetchAttributes();
    return () => {
      cancelled = true;
    };
  }, [buildingId, session]);

  if (!buildingId || attributes.length === 0) return null;

  const optionLabel = (attribute: SelectableBuildingAttribute) =>
    attribute.template?.category
      ? `${attribute.name} (${attribute.template.category})`
      : attribute.name;

  return (
    <div className="border-neutral-primary/20 flex flex-col gap-2 rounded-md border p-3">
      <header className="flex items-center gap-1 text-sm font-medium">
        <Cube weight="duotone" size={18} />
        <p>Attribuut (optioneel)</p>
      </header>
      <EditableSelectComponent
        editMode={true}
        label="Selecteer attribuut"
        value={value != null ? String(value) : ""}
        options={[
          { value: "", label: "Geen attribuut" },
          ...attributes.map((attribute) => ({
            value: String(attribute.id),
            label: optionLabel(attribute),
          })),
        ]}
        onChange={(newValue: string) => {
          const attribute = attributes.find((a) => String(a.id) === newValue) || null;
          onSelect(attribute);
        }}
      />
      {value != null && (
        <div className="text-neutral-secondary-mid flex items-start gap-1 text-xs">
          <Info size={14} weight="duotone" className="mt-0.5 shrink-0" />
          <span>
            Het document wordt opgeslagen in de SharePoint-map van dit attribuut en
            blijft eraan gekoppeld.
          </span>
        </div>
      )}
    </div>
  );
};
